import { AlertCircle, AlertTriangle, ChevronRight, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import type {
  AiAccessPromptView,
  AiGuardNoticeView,
  ContinuityIssueView,
} from "@/features/ai";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import { cn } from "@/lib/utils";

type ContinuityReviewPanelProps = {
  busy?: boolean;
  errorMessage?: string | null;
  guardNotices?: AiGuardNoticeView[];
  issues?: ContinuityIssueView[];
  noCanonContext?: boolean;
  onPromptAction?: () => void;
  onRunReview?: () => void;
  onOpenWorkspace?: () => void;
  onSelectIssue?: (issueId: string) => void;
  prompt?: AiAccessPromptView | null;
  reviewSummary?: string | null;
  selectedIssueId?: string | null;
  statusLabel?: string | null;
};

export function ContinuityReviewPanel({
  busy = false,
  errorMessage = null,
  guardNotices = [],
  issues = [],
  noCanonContext = false,
  onPromptAction,
  onOpenWorkspace,
  onRunReview,
  onSelectIssue,
  prompt = null,
  reviewSummary = null,
  selectedIssueId = null,
  statusLabel = null,
}: ContinuityReviewPanelProps) {
  const { t } = useTranslation();
  const continuityReviewCreditCost = useRuntimeCreditCost("continuity_review");
  const requestBlocked =
    prompt?.kind === "budget-restricted" || prompt?.kind === "wait";
  const selectedIssue = issues.find((issue) => issue.id === selectedIssueId) ?? null;
  const previewIssues = issues.slice(0, 4);
  const statusCounts = {
    acknowledged: issues.filter((issue) => issue.resolutionStatus === "acknowledged").length,
    dismissed: issues.filter((issue) => issue.resolutionStatus === "dismissed").length,
    open: issues.filter((issue) => issue.resolutionStatus === "open").length,
    resolved: issues.filter((issue) => issue.resolutionStatus === "resolved").length,
  };

  function getSeverityIcon(issue: ContinuityIssueView) {
    switch (issue.severity) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-[#e64d43]" />;
      case "medium":
        return <AlertTriangle className="h-4 w-4 text-[#d8921b]" />;
      case "low":
      default:
        return <AlertCircle className="h-4 w-4 text-[#4971f5]" />;
    }
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          {t("story.ai.continuity.eyebrow")}
        </p>
        <h3 className="text-xl text-foreground">{t("story.ai.continuity.title")}</h3>
        <p className="text-sm leading-6 text-muted-foreground">
          {t("story.ai.continuity.body")}
        </p>
      </div>

      {errorMessage ? (
        <div
          className="rounded-[1.1rem] border border-destructive/25 bg-destructive/5 px-4 py-4 text-sm text-destructive"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      {prompt ? (
        <div className="space-y-3 rounded-[1.1rem] border border-amber-300/40 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
          <p>{t(prompt.descriptionKey, prompt.descriptionValues)}</p>
          {prompt.ctaLabelKey && onPromptAction ? (
            <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
              {t(prompt.ctaLabelKey)}
            </Button>
          ) : null}
        </div>
      ) : null}

      <AiGuardNoticeList notices={guardNotices} />

      {reviewSummary ? (
        <div className="rounded-[1.2rem] border border-[#e7d9c8] bg-[#f8f2e9] px-4 py-4 text-sm leading-6 text-muted-foreground">
          {reviewSummary}
        </div>
      ) : null}

      {noCanonContext ? (
        <div className="rounded-[1.2rem] border border-dashed border-ink/10 bg-[#f6eee1]/65 px-4 py-4 text-sm leading-6 text-muted-foreground">
          {t("story.ai.continuity.noCanon")}
        </div>
      ) : issues.length === 0 ? (
        <div className="rounded-[1.2rem] border border-dashed border-ink/10 bg-[#f6eee1]/65 px-4 py-4 text-sm leading-6 text-muted-foreground">
          {busy
            ? t("story.ai.continuity.loading")
            : t("story.ai.continuity.empty")}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[1.35rem] border border-[#e4d6c6] bg-[#fffdf9]">
          <div className="border-b border-[#ece2d6] px-4 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-[#3b3129] text-[#fff9f0]" variant="secondary">
                {t("story.ai.continuity.filters.all", { defaultValue: "All" })}
              </Badge>
              <Badge variant="outline">
                {t("story.ai.continuity.resolutions.open")} {statusCounts.open}
              </Badge>
              <Badge variant="outline">
                {t("story.ai.continuity.resolutions.acknowledged")} {statusCounts.acknowledged}
              </Badge>
              <Badge variant="outline">
                {t("story.ai.continuity.resolutions.resolved")} {statusCounts.resolved}
              </Badge>
            </div>
          </div>

          <div className="divide-y divide-[#ece2d6]">
            {previewIssues.map((issue) => {
              const isSelected = issue.id === selectedIssue?.id;

              return (
                <button
                  className={cn(
                    "w-full px-4 py-4 text-left transition-colors hover:bg-[#fbf6ee]",
                    isSelected && "bg-[#f7f0e6]",
                  )}
                  key={issue.id}
                  onClick={() => {
                    onSelectIssue?.(issue.id);
                    onOpenWorkspace?.();
                  }}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em]">
                        <span className="inline-flex items-center gap-2 text-foreground">
                          {getSeverityIcon(issue)}
                          {issue.severityLabel}
                        </span>
                        <Badge variant="outline">{issue.resolutionStatusLabel}</Badge>
                      </div>
                      <p className="line-clamp-2 text-sm leading-6 text-foreground">
                        {issue.description}
                      </p>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-[#ece2d6] px-4 py-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {t("story.ai.continuity.issueCount", {
                count: issues.length,
                defaultValue: "{{count}} stored issues",
              })}
            </p>
            {onOpenWorkspace ? (
              <Button onClick={onOpenWorkspace} size="sm" type="button" variant="ghost">
                {t("story.ai.continuity.openWorkspace", {
                  defaultValue: "Open full review",
                })}
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-ink/10 pt-4">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {statusLabel ?? t("story.ai.continuity.creditStatus", {
            cost: continuityReviewCreditCost,
            remaining: 0,
          })}
        </span>
        <Button
          disabled={busy || requestBlocked}
          onClick={onRunReview}
          type="button"
          variant="outline"
        >
          <ShieldCheck className="h-4 w-4" />
          {busy ? t("story.ai.continuity.running") : t("story.ai.continuity.runAction")}
        </Button>
      </div>
    </section>
  );
}
